import React, { useState, useRef, KeyboardEvent, useEffect } from 'react';
import NumberedInput from '@components/NumberedInput/NumberedInput';
import { Container } from './RecoveryPhrase.styles';
import { useTranslation } from 'react-i18next';
import { getPlatform } from '@lib/telegram';

interface RecoveryPhraseProps {
  mode: 'input' | 'display';
  phrases?: string[];
  onPhrasesChange?: (phrases: string[]) => void;
  className?: string;
  onEnterNewLine?: () => void;
}

/**
 * Checks if all phrases in the recovery phrase form are filled
 */
export const isFormComplete = (phrases: string[]): boolean => {
  return phrases.every(value => value.trim() !== '');
};

const RecoveryPhrase: React.FC<RecoveryPhraseProps> = ({
  mode = 'input',
  phrases: externalPhrases,
  onPhrasesChange,
  className,
  onEnterNewLine = () => {},
}) => {
  const { t } = useTranslation();
  const isDisplayMode = mode === 'display';
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [internalPhrases, setInternalPhrases] = useState<string[]>(Array(12).fill(''));
  const inputRefs = useRef<(HTMLInputElement | null)[]>(Array(12).fill(null));
  const lastScrolledIndexRef = useRef<number>(-1); // Track which input we last scrolled for

  // Use external phrases if provided, otherwise use internal state
  const phrases = externalPhrases || internalPhrases;

  const handleInputChange = (index: number, value: string) => {
    if (isDisplayMode) return;
    
    const newValues = [...phrases];
    newValues[index] = value;
    
    if (externalPhrases && onPhrasesChange) {
      // Controlled component - update parent
      onPhrasesChange(newValues);
    } else {
      // Uncontrolled component - update internal state
      setInternalPhrases(newValues);
    }
  };

  const handleFocus = (index: number) => {
    if (!isDisplayMode) {
      setActiveIndex(index);

      const isAndroid = getPlatform() === 'android';

      // Android-only: Scroll when focusing on odd-numbered inputs (even indices: 2, 4, 6, 8, 10)
      // Skip index 0 (input #1)
      // Only scroll once per input (don't scroll again if already scrolled for this index)
      if (isAndroid && index % 2 === 0 && index !== 0 && lastScrolledIndexRef.current !== index) {
        lastScrolledIndexRef.current = index;
        onEnterNewLine();
      }
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();

      const isAndroid = getPlatform() === 'android';

      // iOS/Desktop: Scroll on Enter/Tab for even-numbered inputs
      // Android: Skip this since we handle scroll on focus instead
      if (!isAndroid && index % 2 !== 0) {
        onEnterNewLine()
      }

      // Move to the next input if available
      if (index < 11) {
        setActiveIndex(index + 1);
        // Focus the next input
        setTimeout(() => {
          inputRefs.current[index + 1]?.focus();
        }, 0);
      } else {
        // This is the last input, blur to close keyboard
        inputRefs.current[index]?.blur();
      }
    }
  };

  // Effect to focus the active input when it changes
  useEffect(() => {
    if (!isDisplayMode && inputRefs.current[activeIndex]) {
      inputRefs.current[activeIndex]?.focus();
    }
  }, [activeIndex, isDisplayMode]);

  return (
    <div className={className}>
      <Container>
        {Array.from({ length: 12 }).map((_, index) => (
          <NumberedInput
            key={index}
            number={index + 1}
            isActive={activeIndex === index && !isDisplayMode}
            onActivate={() => !isDisplayMode && setActiveIndex(index)}
            value={phrases[index]}
            onChange={(e) => handleInputChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onFocus={() => handleFocus(index)}
            placeholder={isDisplayMode ? '' : t('components.recoveryPhrase.placeholder')}
            displayOnly={isDisplayMode}
            ref={(el) => { inputRefs.current[index] = el; }}
          />
        ))}
      </Container>
    </div>
  );
};

export type { RecoveryPhraseProps };
export default RecoveryPhrase;
